import React, { useState, useEffect } from "react";
import { Eye, Trash2, X,  Mail, Phone, GraduationCap, MapPin, Calendar,  School, Book } from "lucide-react";
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase"; // Adjust path as needed

const StudentApproval = () => {
  const [students, setStudents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check screen size for mobile detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Firebase real-time listener
  useEffect(() => {
    setDataLoading(true);
    setError(null);

    const q = query(
      collection(db, "registered_student"),
      orderBy("registrationDate", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const data = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setStudents(data);
          setDataLoading(false);
        } catch (err) {
          console.error("Error processing student data:", err);
          setError("Failed to load student data");
          setDataLoading(false);
        }
      },
      (err) => {
        console.error("Error fetching students:", err);
        setError("Failed to connect to database");
        setDataLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleView = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!studentToDelete) return;
    
    setLoading(true);
    try {
      await deleteDoc(doc(db, "registered_student", studentToDelete.id));
      setShowDeleteConfirm(false);
      setStudentToDelete(null);
    } catch (error) {
      console.error("Error deleting student:", error);
      setError("Failed to delete student. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setStudentToDelete(null);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "N/A";
    }
  };

  const formatBirthDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return "N/A";
    }
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '1.6',
      color: '#1f2937',
      backgroundColor: '#f9fafb',
      minHeight: '100vh',
      padding: isMobile ? '0.5rem' : '1rem',
      boxSizing: 'border-box',
      fontSize: '14px'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .table-hover:hover {
            background-color: #f9fafb;
          }
          
          .button-hover:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
          }
          
          .modal-backdrop {
            backdrop-filter: blur(4px);
          }
          
          .card-container {
            background: white;
            border-radius: ${isMobile ? '8px' : '12px'};
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            max-width: 1400px;
            margin: 0 auto;
          }
          
          .mobile-card {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            margin-bottom: 1rem;
            overflow: hidden;
          }
          
          .mobile-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1rem;
            font-weight: 600;
            font-size: 1rem;
          }
          
          .mobile-card-body {
            padding: 1rem;
          }
          
          .mobile-card-field {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0;
            border-bottom: 1px solid #e5e7eb;
          }
          
          .mobile-card-field:last-child {
            border-bottom: none;
          }
          
          .mobile-card-label {
            font-weight: 600;
            color: #374151;
            font-size: 0.875rem;
          }
          
          .mobile-card-value {
            color: #6b7280;
            font-size: 0.875rem;
            text-align: right;
            max-width: 60%;
            word-break: break-word;
          }
          
          .mobile-actions {
            display: flex;
            gap: 0.5rem;
            padding: 1rem;
            background: #f9fafb;
            border-top: 1px solid #e5e7eb;
          }
          
          @media (max-width: 768px) {
            .modal-content {
              margin: 0.5rem;
              max-height: calc(100vh - 1rem);
            }
            
            .details-grid {
              grid-template-columns: 1fr;
            }
            
            .profile-section {
              flex-direction: column;
              text-align: center;
              gap: 1rem;
            }
            
            .confirm-buttons {
              flex-direction: column;
            }
          }
        `}
      </style>

      <div className="card-container">
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: '700',
            margin: '0 0 0.5rem 0',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            Student Management
          </h1>
          <p style={{
            fontSize: isMobile ? '0.875rem' : '1rem',
            opacity: '0.9',
            margin: '0',
            fontWeight: '400'
          }}>
            Review and manage registered students
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            padding: '1rem',
            borderRadius: '8px',
            margin: '1rem',
            border: '1px solid #fecaca',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {dataLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '3rem',
            color: '#6b7280'
          }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              border: '2px solid #e5e7eb',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '0.5rem'
            }}></div>
            Loading students...
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            {!isMobile && (
              <div style={{ overflowX: 'auto', backgroundColor: '#ffffff' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                  minWidth: '600px'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderBottom: '2px solid #e5e7eb',
                        fontSize: '0.875rem'
                      }}>Student Name</th>
                      <th style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderBottom: '2px solid #e5e7eb',
                        fontSize: '0.875rem'
                      }}>Email</th>
                      <th style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderBottom: '2px solid #e5e7eb',
                        fontSize: '0.875rem'
                      }}>Preferred Course Category</th>
                      <th style={{
                        padding: '1rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderBottom: '2px solid #e5e7eb',
                        fontSize: '0.875rem'
                      }}>Registration Date</th>
                      <th style={{
                        padding: '1rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        backgroundColor: '#f9fafb',
                        borderBottom: '2px solid #e5e7eb',
                        fontSize: '0.875rem'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{
                          padding: '1rem',
                          borderBottom: '1px solid #e5e7eb',
                          textAlign: 'center',
                          color: '#6b7280',
                          fontStyle: 'italic'
                        }}>
                          No registered students found.
                        </td>
                      </tr>
                    ) : (
                      students.map((student) => (
                        <tr key={student.id} className="table-hover">
                          <td style={{
                            padding: '1rem',
                            borderBottom: '1px solid #e5e7eb',
                            fontWeight: '600'
                          }}>
                            {student.fullName || "N/A"}
                          </td>
                          <td style={{
                            padding: '1rem',
                            borderBottom: '1px solid #e5e7eb',
                            color: '#6b7280'
                          }}>
                            {student.email || "N/A"}
                          </td>
                          <td style={{
                            padding: '1rem',
                            borderBottom: '1px solid #e5e7eb',
                            color: '#6b7280'
                          }}>
                            {student.course || "N/A"}
                          </td>
                          <td style={{
                            padding: '1rem',
                            borderBottom: '1px solid #e5e7eb',
                            color: '#6b7280'
                          }}>
                            {formatDate(student.registrationDate)}
                          </td>
                          <td style={{
                            padding: '1rem',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center'
                          }}>
                            <button
                              onClick={() => handleView(student)}
                              className="button-hover"
                              style={{
                                padding: '0.5rem',
                                margin: '0 0.25rem',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(student)}
                              className="button-hover"
                              style={{
                                padding: '0.5rem',
                                margin: '0 0.25rem',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                              title="Delete Student"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile Card View */}
            {isMobile && (
              <div style={{ padding: '1rem' }}>
                {students.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    fontStyle: 'italic',
                    padding: '2rem'
                  }}>
                    No registered students found.
                  </div>
                ) : (
                  students.map((student) => (
                    <div key={student.id} className="mobile-card">
                      <div className="mobile-card-header">
                        {student.fullName || "N/A"}
                      </div>
                      <div className="mobile-card-body">
                        <div className="mobile-card-field">
                          <span className="mobile-card-label">Email:</span>
                          <span className="mobile-card-value">{student.email || "N/A"}</span>
                        </div>
                        <div className="mobile-card-field">
                          <span className="mobile-card-label">Preferred Course Category:</span>
                          <span className="mobile-card-value">{student.course || "N/A"}</span>
                        </div>
                        <div className="mobile-card-field">
                          <span className="mobile-card-label">Registration:</span>
                          <span className="mobile-card-value">{formatDate(student.registrationDate)}</span>
                        </div>
                      </div>
                      <div className="mobile-actions">
                        <button
                          onClick={() => handleView(student)}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Eye size={16} />
                          View Details
                        </button>
                        <button
                          onClick={() => handleDeleteClick(student)}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* View Modal */}
      {showModal && selectedStudent && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1000',
            padding: isMobile ? '0.5rem' : '1rem'
          }}
          onClick={handleCloseModal}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: isMobile ? '12px' : '16px',
              maxWidth: isMobile ? '100%' : '1000px',
              width: '100%',
              maxHeight: isMobile ? 'calc(100vh - 1rem)' : '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e5e7eb'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: isMobile ? '1rem' : '1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                fontWeight: '700'
              }}>
                Student Details
              </h2>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <X size={isMobile ? 20 : 24} />
              </button>
            </div>

            <div
              className="profile-section"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <div style={{
                width: isMobile ? '3rem' : '4rem',
                height: isMobile ? '3rem' : '4rem',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                flexShrink: '0'
              }}>
                {selectedStudent.fullName?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <h3 style={{
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0'
                }}>
                  {selectedStudent.fullName || "N/A"}
                </h3>
                <p style={{
                  margin: '0',
                  color: '#6b7280',
                  fontSize: isMobile ? '0.8rem' : '0.875rem'
                }}>
                  {selectedStudent.course || "N/A"}
                </p>
              </div>
            </div>
            <div style={{
              marginTop: '1rem',

              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              padding: '1rem',
              backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Mail size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Email</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {selectedStudent.email || "N/A"}
                  </p>
                </div>
              </div>

            <div
              className="details-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
                padding: isMobile ? '1rem' : '1.5rem'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Book size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Preferred Course Category</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {selectedStudent.course || "N/A"}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Phone size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Phone</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {selectedStudent.phoneNumber || "N/A"}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <GraduationCap size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Education</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {selectedStudent.education || "N/A"}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <School size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>College</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {selectedStudent.collegeName || "N/A"}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <MapPin size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Address</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {selectedStudent.address || "N/A"}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Calendar size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Date of Birth</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {formatBirthDate(selectedStudent.dateOfBirth)}
                  </p>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Calendar size={16} style={{ color: '#6b7280', flexShrink: '0', marginTop: '0.125rem' }} />
                <div>
                  <p style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 0.25rem 0'
                  }}>Registration Date</p>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#1f2937',
                    fontWeight: '500',
                    margin: '0',
                    wordBreak: 'break-word'
                  }}>
                    {formatBirthDate(selectedStudent.registrationDate)}
                  </p>
                </div>
              </div>



            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && studentToDelete && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1000',
            padding: isMobile ? '0.5rem' : '1rem'
          }}
          onClick={handleDeleteCancel}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: isMobile ? '12px' : '16px',
              maxWidth: isMobile ? '100%' : '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e5e7eb'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: isMobile ? '1.5rem 1rem 1rem' : '2rem 1.5rem 1rem' }}>
              <div style={{
                width: isMobile ? '3rem' : '4rem',
                height: isMobile ? '3rem' : '4rem',
                borderRadius: '50%',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                border: '1px solid #fecaca'
              }}>
                <Trash2 size={isMobile ? 20 : 24} style={{ color: '#ef4444' }} />
              </div>
              <h3 style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: '0 0 0.5rem 0'
              }}>
                Delete Student
              </h3>
              <p style={{
                color: '#6b7280',
                margin: '0',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                lineHeight: '1.5'
              }}>
                Are you sure you want to delete <strong>{studentToDelete.fullName}</strong>? 
                This action cannot be undone.
              </p>
            </div>
            <div
              className="confirm-buttons"
              style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center',
                padding: '1.5rem',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  minWidth: isMobile ? '100px' : '120px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease',
                  minWidth: isMobile ? '100px' : '120px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  opacity: loading ? '0.7' : '1'
                }}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentApproval;